import React from 'react';
import autobind from 'autobind-decorator';
import Base from 'client/components/base';

export default class BaseAnimation extends Base {
  constructor(props) {
    super(props);

    this.animationsByStateProperty = {};
  }

  animate(stateProperty, target, duration, method, onTargetCallback) {
    if (typeof stateProperty === 'object') {
      let parameters = stateProperty;
      stateProperty = parameters.property;
      target = parameters.target;
      duration = parameters.duration;
      method = parameters.method;
      onTargetCallback = parameters.onComplete;
    }
    if (this.hasAnimation(stateProperty)) {
      let animation = this.getAnimation(stateProperty);
      animation.method = method;
      animation.duration = duration;
      animation.target = target;
      animation.onTargetCallback = onTargetCallback;
      animation.start();
    } else {
      this.createAnimation(
        stateProperty,
        target,
        duration,
        method,
        onTargetCallback
      );
    }
  }

  resetAnimation(stateProperty, target) {
    if (this.hasAnimation(stateProperty)) {
      let animation = this.getAnimation(stateProperty);
      animation.target = target;
      animation.start();
      animation.stop();
    } else {
      let animation = this.createAnimation(stateProperty, target);
      animation.start();
      animation.stop();
    }
  }

  hasAnimation(stateProperty) {
    return this.animationsByStateProperty[stateProperty] ? true : false;
  }

  getAnimation(stateProperty) {
    return this.animationsByStateProperty[stateProperty];
  }

  createAnimation(stateProperty, target, duration, method, onTargetCallback) {
    return (this.animationsByStateProperty[stateProperty] = new Animation(
      this,
      stateProperty,
      target,
      duration,
      method,
      onTargetCallback
    ));
  }

  componentWillUnmount() {
    for (let stateProperty in this.animationsByStateProperty) {
      let animation = this.getAnimation(stateProperty);
      animation.destroy();
    }
  }
}

// This is the for all animations RAF handler
// and stuff needed to keep track of all animations
// Note that animations are only removed from the
// array on destroy right now

let animations = [];
let raf = null;

function updateAnimations() {
  let anyActive = false;
  for (var a = 0; a < animations.length; a++) {
    let animation = animations[a];
    if (animation.active) {
      animation.update();
      anyActive = true;
    }
  }

  if (anyActive) {
    raf = requestAnimationFrame(updateAnimations);
  } else {
    raf = null;
  }
}

function addAnimation(animation) {
  if (animations.indexOf(animation) === -1) {
    animations.push(animation);
  }

  if (!raf) {
    raf = requestAnimationFrame(updateAnimations);
  }
}

function removeAnimation(animation) {
  let i = animations.indexOf(animation);
  if (i > -1) {
    animations.slice(i, 1);
  }
}

// Shared easing functions
let methods = {
  // easing functions
  // t = current time
  // b = start value
  // c = change in value
  // d = duration

  easeInOut: function(t, b, c, d) {
    t /= d / 2;
    if (t < 1) return c / 2 * t * t + b;
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
  },

  easeOut: function(t, b, c, d) {
    t /= d;
    return -c * t * (t - 2) + b;
  },

  easeIn: function(t, b, c, d) {
    t /= d;
    return c * t * t + b;
  },
};

// The animation class

class Animation {
  constructor(
    component,
    stateProperty,
    target,
    duration,
    method,
    onTargetCallback
  ) {
    this.component = component;
    this.value = {
      start: this.component.state[stateProperty],
      target: null,
      delta: null,
      method: null,
      onTargetCallback: null,
    };
    this.time = {
      start: null,
      current: 0,
      duration: null,
    };
    this.state = {};
    this.stateProperty = stateProperty;
    this.state[this.stateProperty] = this.value.start;

    this.target = target;
    this.duration = duration;
    this.method = method;
    this.onTargetCallback = onTargetCallback;
    this.active = false;

    this.start();
  }

  start() {
    if (!this.active) {
      this.active = true;

      this.time.start = Date.now();
      this.time.current = 0;

      addAnimation(this);
    }
  }

  stop() {
    if (this.active) {
      this.active = false;
      this.setState(this.value.target);
    }
  }

  set target(newTarget) {
    if (this.value.target === null || typeof newTarget !== 'undefined') {
      this.value.start = this.component.state[this.stateProperty];
      this.value.target = newTarget;
      this.value.delta = this.value.target - this.value.start;
    }
  }

  set duration(newDuration) {
    if (this.time.duration === null || typeof newDuration !== 'undefined') {
      this.time.duration = newDuration || 200;
    }
  }

  set method(newMethod) {
    if (this.value.method === null || typeof newMethod !== 'undefined') {
      if (newMethod && methods[newMethod]) {
        this.value.method = methods[newMethod];
      } else if (newMethod && methods[newMethod] === undefined) {
        this.value.method = methods.easeOut;
      } else {
        this.value.method = methods.easeOut;
      }
    }
  }

  set onTargetCallback(newCallback) {
    if (
      this.value.onTargetCallback === null ||
      typeof newCallback === 'function'
    ) {
      this.value.onTargetCallback = newCallback || null;
    }
  }

  setState(value) {
    this.state[this.stateProperty] = value;
    this.component.setState(this.state);
  }

  update() {
    if (this.active) {
      this.time.current = Date.now() - this.time.start;
      if (this.time.current < this.time.duration) {
        let value = this.value.method(
          this.time.current,
          this.value.start,
          this.value.delta,
          this.time.duration
        );
        this.setState(value);
      } else {
        this.stop();
        if (this.value.onTargetCallback) {
          this.value.onTargetCallback();
          this.value.onTargetCallback = null;
        }
      }
    }
  }

  destroy() {
    this.stop();
    removeAnimation(this);

    // release all references
    this.component = undefined;
    this.value.method = undefined;
    this.value.onTargetCallback = undefined;
    this.value = undefined;
    this.time = undefined;
    this.state = undefined;
  }
}



// WEBPACK FOOTER //
// ./client/components/base-animation.es6